import React, { useEffect, useRef, useState } from 'react';
import { X, Save, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import DarkTooltip from '../ui/DarkTooltip';
import { EMPTY_FILTERS } from './hooks/useNicheFinderFilters';
import { parseSubsInput } from './utils';

/**
 * NicheFinderFiltersModal — Modal de filtros avancados.
 *
 * COMPLEMENTA os filtros inline (Sprint 3 v5):
 *   - Idioma / Categoria ficam SO na barra inline
 *   - Modal foca em refinamentos: tri-state monetizacao/faceless,
 *     ranges finos com inputs numericos, score minimo de faceless,
 *     e combinacoes salvas em localStorage.
 *
 * Estado INTERNO temporario — mudancas nos controles nao afetam a lista
 * de canais ate o user clicar "Aplicar filtros". Evita re-renderizacao
 * pesada do grid em cada toggle/slider.
 */

const SAVED_KEY = 'niche_finder_saved_filters';

function loadSaved() {
  try {
    const raw = localStorage.getItem(SAVED_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function persistSaved(list) {
  try { localStorage.setItem(SAVED_KEY, JSON.stringify(list)); } catch { /* noop */ }
}

function uuid() {
  // 36-char base seguro pra ids client-side (nao precisa cripto)
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function snapshotFiltersForSave(f) {
  // Salva apenas os campos do modal (sem search/sort/languages/categories).
  // Idioma e Categoria sao controlados pela barra inline.
  const {
    subsMin, subsMax, viewsMin, viewsMax, videosMin, videosMax,
    monetized, addedAfter,
  } = f;
  return {
    subsMin: subsMin ?? null,
    subsMax: subsMax ?? null,
    viewsMin: viewsMin ?? null,
    viewsMax: viewsMax ?? null,
    videosMin: videosMin ?? null,
    videosMax: videosMax ?? null,
    monetized: monetized ?? null,
    addedAfter: addedAfter || 'any',
  };
}

function filtersEqual(a, b) {
  if (!a || !b) return false;
  return JSON.stringify(snapshotFiltersForSave(a))
       === JSON.stringify(snapshotFiltersForSave(b));
}


const NicheFinderFiltersModal = ({
  isOpen,
  currentFilters,
  channelsPreviewCount,
  onApply,
  onClose,
}) => {
  const { t } = useTranslation();
  const [local, setLocal] = useState(currentFilters);
  const [saved, setSaved] = useState(loadSaved);
  const [saveNameOpen, setSaveNameOpen] = useState(false);

  // Reseta estado interno toda vez que reabre
  useEffect(() => {
    if (isOpen) {
      setLocal(currentFilters);
      setSaved(loadSaved());
      setSaveNameOpen(false);
    }
  }, [isOpen, currentFilters]);

  if (!isOpen) return null;

  const update = (patch) => setLocal((prev) => ({ ...prev, ...patch }));

  const handleApply = () => {
    onApply(snapshotFiltersForSave(local));
    onClose();
  };

  const handleClearAll = () => {
    setLocal({ ...EMPTY_FILTERS, search: local.search, sortBy: local.sortBy });
  };

  const handleOpenSaveDialog = () => setSaveNameOpen(true);

  const handleConfirmSaveName = (name) => {
    const trimmed = (name || '').trim().slice(0, 50);
    if (!trimmed) return;
    const entry = {
      id: uuid(),
      name: trimmed,
      filters: snapshotFiltersForSave(local),
      created_at: new Date().toISOString(),
    };
    const next = [entry, ...saved].slice(0, 50);
    setSaved(next);
    persistSaved(next);
    setSaveNameOpen(false);
  };

  const handleApplySaved = (entry) => {
    // Defesa: entry.filters pode estar undefined / corrompido em localStorage
    // antigo (ex: schema antes do snapshotFiltersForSave atual).
    const patch = entry && entry.filters ? entry.filters : {};
    setLocal((prev) => ({ ...prev, ...patch }));
  };

  // Delete in-line (sem window.confirm — fluxo todo-dark, sem dialog nativo).
  const handleDeleteSaved = (id) => {
    const next = saved.filter((s) => s.id !== id);
    setSaved(next);
    persistSaved(next);
  };

  // onClick do overlay so dispara onClose se o click foi NO PROPRIO overlay
  // (nao em filho que propagou). Evita fechar o modal quando bubbling de
  // botoes internos (incluindo pills de filtros salvos) chega aqui em
  // situacoes onde o stopPropagation do wrapper falhou.
  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="dp-modal-overlay"
      onClick={handleOverlayClick}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.6)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: 16,
        animation: 'dp-modal-overlay 0.2s ease',
        fontFamily: 'var(--dp-font)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--dp-bg-card)',
          border: '1px solid var(--dp-border-medium)',
          borderRadius: 12,
          width: '100%',
          maxWidth: 720,
          maxHeight: '85vh',
          display: 'flex',
          flexDirection: 'column',
          boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
          animation: 'dp-modal-scale 0.25s cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        {/* ===== Header ===== */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '18px 24px',
            borderBottom: '1px solid var(--dp-border-subtle)',
          }}
        >
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 600, color: 'var(--dp-text-primary)' }}>
            {t('viralNiches.advanced_filters_title')}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('viralNiches.cancel')}
            style={{
              width: 32, height: 32,
              borderRadius: 8,
              border: '1px solid var(--dp-border-subtle)',
              background: 'transparent',
              color: 'var(--dp-text-secondary)',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={16} />
          </button>
        </div>

        {/* ===== Body ===== */}
        <div
          className="dp-thin-scrollbar"
          style={{
            padding: '18px 24px',
            overflowY: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
          }}
        >
          {/* Saved filters */}
          <SavedFiltersSection
            saved={saved}
            currentSnap={local}
            onApply={handleApplySaved}
            onSave={handleOpenSaveDialog}
            onDelete={handleDeleteSaved}
          />

          {/* Status: monetizacao tri-state. Complementa o toggle inline (que
              so define true), permitindo filtrar canais EXPLICITAMENTE
              nao-monetizados. (Pacote 7.6: faceless removido — base toda e
              faceless.) */}
          <FilterCard title={t('viralNiches.filter_card_status')}>
            <Field label={t('viralNiches.filter_card_monetization')}>
              <RadioPills
                value={local.monetized}
                onChange={(v) => update({ monetized: v })}
                options={[
                  { value: null,  label: t('viralNiches.filter_any') },
                  { value: true,  label: t('viralNiches.filter_yes') },
                  { value: false, label: t('viralNiches.filter_no') },
                ]}
              />
            </Field>
          </FilterCard>

          {/* Inscritos */}
          <FilterCard title={t('viralNiches.filter_card_subs')}>
            <RangeInputs
              minLabel={t('viralNiches.filter_from')}
              maxLabel={t('viralNiches.filter_to')}
              min={local.subsMin}
              max={local.subsMax}
              onMinChange={(v) => update({ subsMin: v })}
              onMaxChange={(v) => update({ subsMax: v })}
            />
          </FilterCard>

          {/* Views */}
          <FilterCard title={t('viralNiches.filter_card_views')}>
            <RangeInputs
              minLabel={t('viralNiches.filter_from')}
              maxLabel={t('viralNiches.filter_to')}
              min={local.viewsMin}
              max={local.viewsMax}
              onMinChange={(v) => update({ viewsMin: v })}
              onMaxChange={(v) => update({ viewsMax: v })}
            />
          </FilterCard>

          {/* Nº de vídeos — achar canais com poucos vídeos e muitas views */}
          <FilterCard title={t('viralNiches.filter_card_videos')}>
            <RangeInputs
              minLabel={t('viralNiches.filter_from')}
              maxLabel={t('viralNiches.filter_to')}
              min={local.videosMin}
              max={local.videosMax}
              onMinChange={(v) => update({ videosMin: v })}
              onMaxChange={(v) => update({ videosMax: v })}
            />
          </FilterCard>

          {/* Adicionado em — janela temporal aplicada sobre imported_at */}
          <FilterCard title={t('viralNiches.filter_card_added')}>
            <RadioPills
              value={local.addedAfter || 'any'}
              onChange={(v) => update({ addedAfter: v })}
              options={[
                { value: 'any',       label: t('viralNiches.added_any') },
                { value: 'last_hour', label: t('viralNiches.added_last_hour') },
                { value: 'last_24h',  label: t('viralNiches.added_last_24h') },
                { value: 'last_7d',   label: t('viralNiches.added_last_7d') },
                { value: 'last_30d',  label: t('viralNiches.added_last_30d') },
              ]}
            />
          </FilterCard>
        </div>

        {/* ===== Footer ===== */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '14px 24px',
            borderTop: '1px solid var(--dp-border-subtle)',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <button
            type="button"
            onClick={handleClearAll}
            className="dp-btn-secondary"
            style={{ padding: '8px 14px', fontSize: 13 }}
          >
            {t('viralNiches.clear_all')}
          </button>
          <div
            style={{
              fontSize: 12,
              color: 'var(--dp-text-tertiary)',
              flex: 1,
              textAlign: 'center',
            }}
          >
            {t('viralNiches.channels_matching', { count: channelsPreviewCount ?? 0 })}
          </div>
          <button
            type="button"
            onClick={handleApply}
            className="dp-btn-primary"
            style={{ padding: '8px 16px', fontSize: 13 }}
          >
            {t('viralNiches.apply_filters')}
          </button>
        </div>
      </div>

      {/* Nested dialog: nome do filtro salvo (substitui window.prompt) */}
      <SaveNameDialog
        isOpen={saveNameOpen}
        onConfirm={handleConfirmSaveName}
        onCancel={() => setSaveNameOpen(false)}
      />
    </div>
  );
};

const SaveNameDialog = ({ isOpen, onConfirm, onCancel }) => {
  const { t } = useTranslation();
  const [value, setValue] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setValue('');
      // Autofocus apos transicao
      const id = setTimeout(() => inputRef.current && inputRef.current.focus(), 50);
      return () => clearTimeout(id);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onCancel]);

  if (!isOpen) return null;

  const trimmed = value.trim();
  const canSave = trimmed.length > 0 && trimmed.length <= 50;

  // Cancela so quando o click foi NO PROPRIO backdrop (nao filho propagado).
  // Evita que cliques internos cheguem ao modal pai e disparem onClose.
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onCancel();
  };

  return (
    <div
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(6px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000,
        padding: 16,
        animation: 'dp-modal-overlay 0.18s ease',
        fontFamily: 'var(--dp-font)',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        style={{
          background: 'var(--dp-bg-card)',
          border: '1px solid var(--dp-border-medium)',
          borderRadius: 12,
          width: '100%',
          maxWidth: 380,
          padding: 20,
          boxShadow: '0 24px 64px rgba(0,0,0,0.55)',
          animation: 'dp-modal-scale 0.2s cubic-bezier(0.4,0,0.2,1)',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 14,
          }}
        >
          <h4 style={{ margin: 0, fontSize: 15, fontWeight: 600, color: 'var(--dp-text-primary)' }}>
            {t('viralNiches.save_filter_modal_title')}
          </h4>
          <button
            type="button"
            onClick={onCancel}
            aria-label={t('viralNiches.cancel')}
            style={{
              width: 28, height: 28,
              borderRadius: 6,
              border: '1px solid var(--dp-border-subtle)',
              background: 'transparent',
              color: 'var(--dp-text-secondary)',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <X size={14} />
          </button>
        </div>
        <label
          htmlFor="save-filter-name-input"
          style={{
            display: 'block',
            fontSize: 12,
            color: 'var(--dp-text-secondary)',
            marginBottom: 6,
          }}
        >
          {t('viralNiches.save_filter_modal_label')}
        </label>
        <input
          ref={inputRef}
          id="save-filter-name-input"
          type="text"
          value={value}
          maxLength={50}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && canSave) onConfirm(value);
          }}
          placeholder={t('viralNiches.save_filter_modal_placeholder')}
          className="dp-input"
          style={{ height: 36, marginBottom: 16 }}
        />
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
          }}
        >
          <button
            type="button"
            onClick={onCancel}
            className="dp-btn-secondary"
            style={{ padding: '7px 14px', fontSize: 13 }}
          >
            {t('viralNiches.cancel')}
          </button>
          <button
            type="button"
            onClick={() => onConfirm(value)}
            disabled={!canSave}
            className="dp-btn-primary"
            style={{
              padding: '7px 14px',
              fontSize: 13,
              opacity: canSave ? 1 : 0.5,
              cursor: canSave ? 'pointer' : 'not-allowed',
            }}
          >
            {t('viralNiches.save_filter_modal_save')}
          </button>
        </div>
      </div>
    </div>
  );
};

// ============================================================
// Sub-componentes
// ============================================================

const FilterCard = ({ title, children }) => (
  <div
    style={{
      background: 'var(--dp-bg-secondary)',
      border: '1px solid var(--dp-border-subtle)',
      borderRadius: 10,
      padding: 14,
      display: 'flex',
      flexDirection: 'column',
      gap: 10,
    }}
  >
    <div
      style={{
        fontSize: 10,
        fontWeight: 600,
        letterSpacing: 0.8,
        textTransform: 'uppercase',
        color: 'var(--dp-text-tertiary)',
      }}
    >
      {title}
    </div>
    {children}
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <div
      style={{
        fontSize: 12,
        fontWeight: 500,
        color: 'var(--dp-text-secondary)',
        marginBottom: 6,
      }}
    >
      {label}
    </div>
    {children}
  </div>
);

const RadioPills = ({ value, onChange, options }) => (
  <div style={{ display: 'inline-flex', gap: 6 }}>
    {options.map((opt) => {
      const isSel = value === opt.value;
      return (
        <button
          key={String(opt.value)}
          type="button"
          onClick={() => onChange(opt.value)}
          style={{
            padding: '5px 12px',
            borderRadius: 999,
            border: `0.5px solid ${isSel ? 'rgba(13,174,255,0.30)' : 'var(--dp-border-medium)'}`,
            background: isSel ? 'rgba(13,174,255,0.10)' : 'transparent',
            color: isSel ? 'var(--dp-blue-500)' : 'var(--dp-text-secondary)',
            fontSize: 12,
            fontFamily: 'var(--dp-font)',
            cursor: 'pointer',
          }}
        >
          {opt.label}
        </button>
      );
    })}
  </div>
);

const RangeInputs = ({ minLabel, maxLabel, min, max, onMinChange, onMaxChange }) => {
  const [minStr, setMinStr] = useState(min !== null && min !== undefined ? String(min) : '');
  const [maxStr, setMaxStr] = useState(max !== null && max !== undefined ? String(max) : '');
  useEffect(() => {
    setMinStr(min !== null && min !== undefined ? String(min) : '');
  }, [min]);
  useEffect(() => {
    setMaxStr(max !== null && max !== undefined ? String(max) : '');
  }, [max]);

  const commit = (str, setter) => {
    const parsed = parseSubsInput(str);
    setter(parsed);
  };
  const inputStyle = {
    width: '100%',
    height: 32,
    padding: '0 10px',
    background: 'var(--dp-bg-input)',
    border: '1px solid var(--dp-border-subtle)',
    borderRadius: 8,
    color: 'var(--dp-text-primary)',
    fontSize: 13,
    fontFamily: 'var(--dp-font)',
    outline: 'none',
  };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
      <div>
        <div style={{ fontSize: 11, color: 'var(--dp-text-tertiary)', marginBottom: 4 }}>{minLabel}</div>
        <input
          type="text"
          value={minStr}
          placeholder="—"
          onChange={(e) => setMinStr(e.target.value)}
          onBlur={() => commit(minStr, onMinChange)}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          style={inputStyle}
        />
      </div>
      <div>
        <div style={{ fontSize: 11, color: 'var(--dp-text-tertiary)', marginBottom: 4 }}>{maxLabel}</div>
        <input
          type="text"
          value={maxStr}
          placeholder="—"
          onChange={(e) => setMaxStr(e.target.value)}
          onBlur={() => commit(maxStr, onMaxChange)}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          style={inputStyle}
        />
      </div>
    </div>
  );
};

const SavedFiltersSection = ({ saved, currentSnap, onApply, onSave, onDelete }) => {
  const { t } = useTranslation();
  return (
    <FilterCard title={t('viralNiches.saved_filters_title')}>
      {saved.length === 0 ? (
        <div style={{ fontSize: 12, color: 'var(--dp-text-tertiary)' }}>
          {t('viralNiches.no_saved_filters')}
        </div>
      ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {saved.map((entry) => {
            const isActive = filtersEqual(entry.filters, currentSnap);
            return (
              <span
                key={entry.id}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '4px 4px 4px 10px',
                  borderRadius: 999,
                  background: isActive ? 'rgba(13,174,255,0.10)' : 'var(--dp-bg-input)',
                  border: `0.5px solid ${isActive ? 'rgba(13,174,255,0.30)' : 'var(--dp-border-subtle)'}`,
                  color: isActive ? 'var(--dp-blue-500)' : 'var(--dp-text-primary)',
                  fontSize: 12,
                  fontFamily: 'var(--dp-font)',
                }}
              >
                <button
                  type="button"
                  onClick={(e) => {
                    // stopPropagation extra — defesa contra bubble subir
                    // ate o overlay do modal pai e fechar tudo (bug "tela preta").
                    e.stopPropagation();
                    onApply(entry);
                  }}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    color: 'inherit',
                    cursor: 'pointer',
                    fontSize: 'inherit',
                    fontFamily: 'inherit',
                    padding: 0,
                  }}
                >
                  {entry.name}
                </button>
                <DarkTooltip content={t('viralNiches.confirm_delete_filter')}>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(entry.id);
                    }}
                    aria-label="delete saved filter"
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 999,
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--dp-text-tertiary)',
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0.7,
                    }}
                  >
                    <X size={10} />
                  </button>
                </DarkTooltip>
              </span>
            );
          })}
        </div>
      )}
      <button
        type="button"
        onClick={onSave}
        className="dp-btn-secondary"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 12px',
          fontSize: 12,
          alignSelf: 'flex-start',
          marginTop: saved.length > 0 ? 4 : 0,
        }}
      >
        <Save size={12} />
        {t('viralNiches.save_current_filters')}
      </button>
    </FilterCard>
  );
};

export default NicheFinderFiltersModal;
